import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Zap, ShoppingBag, Package } from 'lucide-react'
import type { ExtendedGroupResponse, GetApiCompaniesCurrentTaxbenefitgroupsParams, OrderType } from '@/api/model'
import {
  getGetApiCompaniesCurrentTaxbenefitgroupsQueryKey,
  useDeleteApiCompaniesCurrentGroupsGroupId,
  useGetApiCompaniesCurrentTaxbenefitgroups,
  useGetApiCompaniesCurrentGroupsFilter,
} from '@/api/generated/company-management-user/company-management-user'
import { notify } from '@/lib/notify'
import { storageKey } from '@/lib/storageKey'
import { TableCard } from '@/components/common/table/TableCard'
import { PaginationFooter } from '@/components/common/table/PaginationFooter'
import { FilterRow } from '@/components/common/filters/FilterRow'
import { FilterSearch } from '@/components/common/filters/FilterSearch'
import { FilterDropdown } from '@/components/common/filters/FilterDropdown'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { GroupsDataTable } from './GroupsDataTable'

const DEFAULT_PAGE_SIZE = 10
const FILTERS_STORAGE_KEY = storageKey('groups_filters')

/** Only the ID filters are persisted (Search/Sort/Pagination deliberately are not). */
type StoredFilters = Pick<
  GetApiCompaniesCurrentTaxbenefitgroupsParams,
  'ActionModuleIds' | 'ServiceModuleIds' | 'ShopModuleIds'
>

function readStoredFilters(): StoredFilters {
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY)
    return raw ? (JSON.parse(raw) as StoredFilters) : {}
  } catch {
    return {}
  }
}

function writeStoredFilters(params: GetApiCompaniesCurrentTaxbenefitgroupsParams) {
  const toStore: StoredFilters = {
    ActionModuleIds: params.ActionModuleIds,
    ServiceModuleIds: params.ServiceModuleIds,
    ShopModuleIds: params.ShopModuleIds,
  }
  localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(toStore))
}

/** Keeps only IDs that the API still knows about; empty selection → undefined. */
function cleanIds(ids: string[] | undefined, valid: Set<string | null | undefined>): string[] | undefined {
  const filtered = ids?.filter((id) => valid.has(id))
  return filtered && filtered.length > 0 ? filtered : undefined
}

interface Props {
  onEdit: (group: ExtendedGroupResponse) => void
  canWrite: boolean
}

export function GroupsTable({ onEdit, canWrite }: Props) {
  const { t } = useTranslation(['groups', 'filters', 'common'])
  const queryClient = useQueryClient()
  const [confirmGroup, setConfirmGroup] = useState<ExtendedGroupResponse | null>(null)
  const [queryParams, setQueryParams] = useState<GetApiCompaniesCurrentTaxbenefitgroupsParams>(() => ({
    Offset: 0,
    Limit: DEFAULT_PAGE_SIZE,
    ...readStoredFilters(),
  }))

  const { data: filterResponse } = useGetApiCompaniesCurrentGroupsFilter()
  const { data, isLoading, isError } = useGetApiCompaniesCurrentTaxbenefitgroups(queryParams)
  const groups = data?.data?.items ?? []
  const totalCount = data?.data?.totalCount ?? 0

  // Once, after the options have loaded: remove IDs that no longer exist.
  const hasCleanedRef = useRef(false)
  useEffect(() => {
    if (hasCleanedRef.current || !filterResponse?.data) return
    hasCleanedRef.current = true

    const options = filterResponse.data
    const validIds = {
      actionModule: new Set((options.actionModules ?? []).map((a) => a.id)),
      serviceModule: new Set((options.serviceModules ?? []).map((s) => s.id)),
      shopModule: new Set((options.shopModules ?? []).map((s) => s.id)),
    }

    setQueryParams((prev) => {
      const cleaned: GetApiCompaniesCurrentTaxbenefitgroupsParams = {
        ...prev,
        ActionModuleIds: cleanIds(prev.ActionModuleIds, validIds.actionModule),
        ServiceModuleIds: cleanIds(prev.ServiceModuleIds, validIds.serviceModule),
        ShopModuleIds: cleanIds(prev.ShopModuleIds, validIds.shopModule),
        Offset: 0,
      }
      writeStoredFilters(cleaned)
      return cleaned
    })
  }, [filterResponse])

  const updateFilters = (patch: Partial<GetApiCompaniesCurrentTaxbenefitgroupsParams>) => {
    setQueryParams((prev) => {
      const next = { ...prev, ...patch, Offset: 0 }
      writeStoredFilters(next)
      return next
    })
  }

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: getGetApiCompaniesCurrentTaxbenefitgroupsQueryKey() })

  const { mutate: deleteGroup, isPending: isDeleting } = useDeleteApiCompaniesCurrentGroupsGroupId({
    mutation: {
      onSuccess: () => {
        notify.success(t('toasts.deleted'))
        setConfirmGroup(null)
        invalidate()
      },
      onError: (error) => notify.apiError(error, t('toasts.deleteError')),
    },
  })

  return (
    <>
      <FilterRow>
        <FilterSearch
          searchLabel={t('filters:searchbar.groupsPage')}
          search={queryParams.Search}
          onChange={(search) => setQueryParams((prev) => ({ ...prev, Search: search, Offset: 0 }))}
        />
        <FilterDropdown
          icon={Zap}
          label={t('filters:dropdown.actionModules')}
          availableOptions={filterResponse?.data?.actionModules ?? []}
          selectedOptions={queryParams.ActionModuleIds}
          onSelect={(ids) => updateFilters({ ActionModuleIds: ids })}
          onReset={() => updateFilters({ ActionModuleIds: undefined })}
        />
        <FilterDropdown
          icon={Package}
          label={t('filters:dropdown.serviceModules')}
          availableOptions={filterResponse?.data?.serviceModules ?? []}
          selectedOptions={queryParams.ServiceModuleIds}
          onSelect={(ids) => updateFilters({ ServiceModuleIds: ids })}
          onReset={() => updateFilters({ ServiceModuleIds: undefined })}
        />
        <FilterDropdown
          icon={ShoppingBag}
          label={t('filters.shopModules')}
          availableOptions={filterResponse?.data?.shopModules ?? []}
          selectedOptions={queryParams.ShopModuleIds}
          onSelect={(ids) => updateFilters({ ShopModuleIds: ids })}
          onReset={() => updateFilters({ ShopModuleIds: undefined })}
        />
      </FilterRow>

      <TableCard>
        <div className="flex-1 overflow-auto scrollbar-custom p-0 relative">
          <GroupsDataTable
            isLoading={isLoading}
            isError={isError}
            groups={groups}
            currentSort={queryParams.Sort}
            currentOrder={queryParams.Order}
            onSort={(newSort?: string, newOrder?: OrderType) =>
              setQueryParams((prev) => ({ ...prev, Sort: newSort, Order: newOrder, Offset: 0 }))}
            onEdit={onEdit}
            onDelete={setConfirmGroup}
            canWrite={canWrite}
          />
        </div>

        <PaginationFooter
          offset={queryParams.Offset}
          limit={queryParams.Limit}
          totalCount={totalCount}
          onOffsetChange={(offset) => setQueryParams((prev) => ({ ...prev, Offset: offset }))}
          onPageSizeChange={(pageSize) => setQueryParams((prev) => ({ ...prev, Limit: pageSize, Offset: 0 }))}
        />
      </TableCard>

      <ConfirmDialog
        open={!!confirmGroup}
        title={t('list.delete.title')}
        description={t('list.delete.description', { name: confirmGroup?.name })}
        confirmLabel={t('common:buttons.delete')}
        confirmButtonVariant="destructive"
        isLoading={isDeleting}
        onConfirm={() => confirmGroup?.id && deleteGroup({ groupId: confirmGroup.id })}
        onClose={() => setConfirmGroup(null)}
      />
    </>
  )
}
